/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreTimeParse - kTimeCore's arithmetic (Add*, working days) and parsing (from JS / ISO / SharePoint / JSON dates).
 * Split out of kCoreTime.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kTimeCore keeps every member as a same-signature delegating member.
 */

import { getCoreUtils, n0, kNotNullOrUndefined } from "./kCoreHandler";
import type { kTimeCore } from "./kCoreTime";

export class kCoreTimeParse {
    /**
     * Add (or subtract) units to this date.
     * interval: "year" | "quarter" | "month" | "week" | "day" | "hour" | "minute" | "second"
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.Add - its gate, try/catch and handler label "kTimeCore.Add" stay verbatim, so error reports are unchanged.
    public static Add = (Self: kTimeCore, interval: string, units: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const SafeUnits: number = n0(units);
            if (SafeUnits === 0) { return; }
            const Ret: Date = Self.ToJsDate();
            const CheckRollover = (): void => {
                if (Ret.getDate() !== Self.ToJsDate().getDate()) { Ret.setDate(0); }
            };
            switch (String(interval).toLowerCase()) {
                case "year":    Ret.setFullYear(Ret.getFullYear() + SafeUnits); CheckRollover(); break;
                case "quarter": Ret.setMonth(Ret.getMonth() + 3 * SafeUnits);  CheckRollover(); break;
                case "month":   Ret.setMonth(Ret.getMonth() + SafeUnits);       CheckRollover(); break;
                case "week":    Ret.setDate(Ret.getDate() + 7 * SafeUnits);    break;
                case "day":     Ret.setDate(Ret.getDate() + SafeUnits);         break;
                case "hour":    Ret.setTime(Ret.getTime() + SafeUnits * 3600000); break;
                case "minute":  Ret.setTime(Ret.getTime() + SafeUnits * 60000);   break;
                case "second":  Ret.setTime(Ret.getTime() + SafeUnits * 1000);    break;
            }
            Self.fromJSDate(Ret);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.Add"); }
    };

    /** Add DaysToAdd calendar days. Returns the new JS Date. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.AddDays - its gate, try/catch and handler label "kTimeCore.AddDays" stay verbatim, so error reports are unchanged.
    public static AddDays = (Self: kTimeCore, DaysToAdd: number): Date | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            Self.Add("day", DaysToAdd);
            return Self.ToJsDate();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.AddDays"); return null; }
    };

    /**
     * Walk forwards or backwards by BUSINESS days, skipping weekends and any date listed as
     * non-working. Added 2026-09-14.
     *
     * A notice period, an SLA and a payment term are all counted this way in real contracts, and
     * doing it by hand goes wrong in the same two places every time:
     *
     *   - ⚠ IT IS A WALK, NOT A MULTIPLICATION. `Days * 7 / 5` is out by a day or more as soon as a
     *     weekend or a holiday falls inside the span, and the error grows with the span.
     *   - ⚠ THE STARTING DATE IS NOT COUNTED, AND A NON-WORKING START IS NOT SKIPPED FOR FREE.
     *     Counting "5 business days from Friday" has to land on the following Friday, which only
     *     happens if each step lands on a working day rather than each step being taken blindly.
     *
     * `NonWorkingDates` are extra dates to treat as non-working - public holidays, a shutdown week -
     * as `yyyy-MM-dd` strings. ⚠ **THIS OBJECT CANNOT KNOW A JURISDICTION'S HOLIDAYS**, so a caller
     * that passes none gets weekends only, which is a different answer and not a wrong one. Say which
     * calendar was used wherever the result is shown.
     *
     * `Days` of 0 returns this date unmoved, even if it is itself non-working: "zero business days
     * from here" is here, and rolling it would invent a movement nobody asked for.
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.AddBusinessDays - its gate, try/catch and handler label "kTimeCore.AddBusinessDays" stay verbatim, so error reports are unchanged.
    public static AddBusinessDays = (Self: kTimeCore, Days: number, NonWorkingDates: string[] = []): Date | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            const Wanted: number = n0(Days);
            if (Wanted === 0) { return Self.ToJsDate(); }
            let Step: number = 1;
            if (Wanted < 0) { Step = -1; }
            let Remaining: number = Math.abs(Wanted);
            // BOUNDED. A caller asking for a million business days, or a NonWorkingDates list that
            // somehow covers every date, must not spin for ever: the loop is capped at a generous
            // multiple of the request and the walk simply stops, leaving a date the caller can see is
            // wrong rather than a process that never answers.
            let Guard: number = Remaining * 10 + 3660;
            while (Remaining > 0 && Guard > 0) {
                Guard = Guard - 1;
                Self.Add("day", Step);
                if (Self.IsWorkingDay(NonWorkingDates)) { Remaining = Remaining - 1; }
            }
            return Self.ToJsDate();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.AddBusinessDays");
            return null;
        }
    };

    /** True where this date is neither a weekend nor one of the supplied non-working dates. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.IsWorkingDay - its gate, try/catch and handler label "kTimeCore.IsWorkingDay" stay verbatim, so error reports are unchanged.
    public static IsWorkingDay = (Self: kTimeCore, NonWorkingDates: string[] = []): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (Self.isWeekend()) { return false; }
            if (NonWorkingDates === null || NonWorkingDates === undefined) { return true; }
            const Mine: string = Self.ToISODate();
            for (const One of NonWorkingDates) {
                if (String(One).trim() === Mine) { return false; }
            }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.IsWorkingDay");
            return false;
        }
    };

    /** Add minutes. Returns the new JS Date. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.AddMinutes - its gate, try/catch and handler label "kTimeCore.AddMinutes" stay verbatim, so error reports are unchanged.
    public static AddMinutes = (Self: kTimeCore, MinutesToAdd: number): Date | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            Self.Add("minute", MinutesToAdd);
            return Self.ToJsDate();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.AddMinutes"); return null; }
    };

    /** Subtract minutes. Returns the new JS Date. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.SubractMinutes - its gate, try/catch and handler label "kTimeCore.SubractMinutes" stay verbatim, so error reports are unchanged.
    public static SubractMinutes = (Self: kTimeCore, MinutesToSubtract: number): Date | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            Self.Add("minute", -MinutesToSubtract);
            return Self.ToJsDate();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SubractMinutes"); return null; }
    };

    /** Subtract calendar days. Returns the new JS Date. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.SubractDays - its gate, try/catch and handler label "kTimeCore.SubractDays" stay verbatim, so error reports are unchanged.
    public static SubractDays = (Self: kTimeCore, DaysToSubtract: number): Date | null => {
        if (getCoreUtils().KErrorMode) { return null; }
        try {
            Self.Add("day", -DaysToSubtract);
            return Self.ToJsDate();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.SubractDays"); return null; }
    };

    // ── Conversion to JS Date ─────────────────────────────────────────────────

    /** Convert to a plain JS Date (local time). Day=0 is treated as day 1. */
    // DOCUMENTED EXCEPTION: pure calendar construction - new Date() from seven numbers cannot
    // throw and touches no I/O, so this carries no error-mode gate or try/catch, exactly as the
    // pre-split kTimeCore.ToJsDate did. The gate added in the 2026-10-21 kCoreTime split broke
    // the late-gate snapshot contract: kTimeCore.AddDays returns Self.ToJsDate() from the tail
    // of an Add that may have latched error mode and MUST return the real snapshot Date, not
    // NaN (LangateSynch DetailUtcDateContract.test.ts:56-58). Ungated also keeps the
    // kCoreTimeNames formatters' WeekOfYear / SecondsAgo / locale math honest during error mode.
    public static ToJsDate = (Self: kTimeCore): Date => {
        let DayOfMonth: number = Self.Day;
        if (DayOfMonth === 0) { DayOfMonth = 1; }
        return new Date(
            Self.Year, Self.Month - 1, DayOfMonth,
            Self.Hour, Self.Minute, Self.Second, Self.MilliSecond
        );
    };

    // ── Population from JS Date / strings ─────────────────────────────────────

    /**
     * Populate all fields from a JS Date object.
     *
     * ⚠ AN INSTANT PUT THROUGH THIS AND READ BACK AS AN INSTANT IS LOSSY FOR ONE HOUR A YEAR, and
     * that is a property of the TYPE rather than a bug in this method — kTimeCore holds LOCAL
     * wall-clock fields, and in the autumn fall-back hour one local time stands for two instants, so
     * `ToJsDate()` (and therefore `ToISOZuluDateTime()`) has to pick, and picks the earlier.
     *
     * MEASURED 2026-08-17 on `Europe/Berlin`, minute by minute across both 2026 European transition
     * days: **60 of 2880 minutes came back exactly one hour early** — first `2026-10-25T01:00:00Z`
     * arriving as `2026-10-25T00:00:00Z` — with an ordinary day as the control at **0 of 1440**, so
     * the probe discriminates and the loss is real rather than a rounding artefact.
     *
     * Nothing to fix here. It is a boundary to know: for a WALL-CLOCK value (a due date, a working
     * day, anything a human reads off a calendar) this is exactly right. For an INSTANT that must
     * survive a round trip to the second — an evidence timestamp, an audit stamp — go straight from
     * the `Date` to `toISOString()` and skip the local fields. `kMimeFieldCore.DateToIso` states the
     * same finding at its own call site, which is where it was found.
     */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.fromJSDate - its gate, try/catch and handler label "kTimeCore.fromJSDate" stay verbatim, so error reports are unchanged.
    public static FromJSDate = (Self: kTimeCore, NewDate: Date): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (kNotNullOrUndefined(NewDate)) {
                Self.Year = NewDate.getFullYear();
                Self.Month = NewDate.getMonth() + 1;
                Self.Day = NewDate.getDate();
                Self.Hour = NewDate.getHours();
                Self.Minute = NewDate.getMinutes();
                Self.Second = NewDate.getSeconds();
                Self.MilliSecond = NewDate.getMilliseconds();
                Self.IsValid = true;
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.fromJSDate"); }
    };

    /** Parse an ISO date string "yyyy-MM-dd" (or "yyyy-MM-dd HH:mm…"). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.FromISODate - its gate, try/catch and handler label "kTimeCore.FromISODate" stay verbatim, so error reports are unchanged.
    public static FromISODate = (Self: kTimeCore, IsoStringDate: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (IsoStringDate !== "") {
                const Parts: string[] = IsoStringDate.split(" ")[0].split("-");
                Self.Year = parseInt(Parts[0]);
                Self.Month = parseInt(Parts[1]);
                Self.Day = parseInt(Parts[2]);
                Self.IsValid = Number.isFinite(Self.Year) && Number.isFinite(Self.Month) && Number.isFinite(Self.Day);
                if (!Self.IsValid) { Self.Year = 0; Self.Month = 0; Self.Day = 0; }
            } else {
                Self.Year = 0; Self.Month = 0; Self.Day = 0; Self.IsValid = false;
            }
            Self.Hour = 0; Self.Minute = 0; Self.Second = 0; Self.MilliSecond = 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.FromISODate"); }
    };

    /** Parse "yyyy-MM-dd HH:mm" (the format produced by ToISODateTime). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.FromISODateTime - its gate, try/catch and handler label "kTimeCore.FromISODateTime" stay verbatim, so error reports are unchanged.
    public static FromISODateTime = (Self: kTimeCore, IsoStringDate: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            // Guard non-string / empty (a not-set date is an EXPECTED condition, not an error) and a
            // MISSING time part — a date-only or T-separated ISO string ("2026-07-20T10:30:00Z") has no
            // " " separator, so MainParts[1] would be undefined and .split(":") threw. Parse the date
            // and default the time to 0 rather than crash. (Space-separated "yyyy-MM-dd HH:mm" is unchanged.)
            if (typeof IsoStringDate === "string" && IsoStringDate !== "") {
                const MainParts: string[] = IsoStringDate.split(" ");
                Self.FromISODate(MainParts[0]);
                Self.Second = 0; Self.MilliSecond = 0;
                if (MainParts.length > 1 && MainParts[1] !== "") {
                    const Parts: string[] = MainParts[1].split(":");
                    Self.Hour = parseInt(Parts[0]);
                    Self.Minute = parseInt(Parts[1]);
                } else {
                    Self.Hour = 0; Self.Minute = 0;
                }
                Self.IsValid = true;
            } else {
                Self.IsValid = false;
                Self.Year = 0; Self.Month = 0;
                Self.Hour = 0; Self.Minute = 0; Self.Second = 0; Self.MilliSecond = 0;
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.FromISODateTime"); }
    };

    /** Parse the ISO 8601 BASIC (separator-less) form "yyyyMMddHHmmss" — e.g. "20200906104207".
     *  This is the shape PowerShell run/tick folder names use (Get-Date -Format yyyyMMddHHmmss).
     *  Shorter strings are accepted and the missing trailing components default to 0, so
     *  "20200906" parses as that date at 00:00:00. Anything shorter than a full date (8 chars),
     *  or a non-string, leaves the instance invalid rather than producing a NaN date. */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.FromCompactDateTime - its gate, try/catch and handler label "kTimeCore.FromCompactDateTime" stay verbatim, so error reports are unchanged.
    public static FromCompactDateTime = (Self: kTimeCore, YearToSec: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (typeof YearToSec !== "string" || YearToSec.length < 8) {
                Self.IsValid = false;
                Self.Year = 0; Self.Month = 0; Self.Day = 0;
                Self.Hour = 0; Self.Minute = 0; Self.Second = 0; Self.MilliSecond = 0;
                return;
            }
            Self.Year = parseInt(YearToSec.substring(0, 4));
            Self.Month = parseInt(YearToSec.substring(4, 6));
            Self.Day = parseInt(YearToSec.substring(6, 8));
            Self.Hour = 0; Self.Minute = 0; Self.Second = 0; Self.MilliSecond = 0;
            if (YearToSec.length >= 10) { Self.Hour = parseInt(YearToSec.substring(8, 10)); }
            if (YearToSec.length >= 12) { Self.Minute = parseInt(YearToSec.substring(10, 12)); }
            if (YearToSec.length >= 14) { Self.Second = parseInt(YearToSec.substring(12, 14)); }
            Self.IsValid = true;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.FromCompactDateTime"); }
    };

    /** Restore from a plain JSON object (e.g. as stored by ToJSONObj on kObject). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.FromJSON - its gate, try/catch and handler label "kTimeCore.FromJSON" stay verbatim, so error reports are unchanged.
    public static FromJSON = (Self: kTimeCore, JSONObj: any): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            Self.Year = JSONObj.Year; Self.Month = JSONObj.Month; Self.Day = JSONObj.Day;
            Self.Hour = JSONObj.Hour; Self.Minute = JSONObj.Minute;
            Self.Second = JSONObj.Second; Self.MilliSecond = JSONObj.MilliSecond;
            Self.IsValid = Self.Year > 0;
            if (typeof JSONObj.IsValid === "boolean") { Self.IsValid = JSONObj.IsValid; }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.FromJSON"); }
    };

    /** Parse a SharePoint REST date string ("2024-01-15T10:30:00Z"). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.FromSPDate - its gate, try/catch and handler label "kTimeCore.FromSPDate" stay verbatim, so error reports are unchanged.
    public static FromSPDate = (Self: kTimeCore, SPDate: string, AllDay?: boolean, _Bias?: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            Self["ClearDate"]();
            if (!SPDate) { return; }
            let Normalised: string = SPDate;
            if (AllDay === true) { Normalised = SPDate.replace("T", " ").replace("Z", ""); }
            const NewDate: Date = new Date(Normalised);
            if (!isNaN(NewDate.getTime())) {
                Self.fromJSDate(NewDate);
            } else {
                Self.IsValid = false;
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.FromSPDate"); }
    };

    /** Parse an SPFx date token ("Date;#2024-01-15T10:30:00Z"). */
    // DOCUMENTED EXCEPTION: moved body of kTimeCore.FromSPFXDate - its gate, try/catch and handler label "kTimeCore.FromSPFXDate" stay verbatim, so error reports are unchanged.
    public static FromSPFXDate = (Self: kTimeCore, SPFXDate: string): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            Self["ClearDate"]();
            if (SPFXDate) {
                const Parts: string[] = SPFXDate.split(";#");
                if (Parts.length === 2) {
                    const NewDate: Date = new Date(Parts[1]);
                    if (!isNaN(NewDate.getTime())) {
                        Self.fromJSDate(NewDate);
                    } else {
                        Self.IsValid = false;
                    }
                }
            } else {
                Self.IsValid = false;
            }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kTimeCore.FromSPFXDate"); }
    };
}
